/* eslint-disable no-console */
/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable max-len */

import { Box, CircularProgress, Divider, Grid, Popover, Select, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { graphqlGetCategories } from '../../../../containers/actions/resource_configuration/category_management';
import useResourceHelper from '../../../../containers/BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Reusable/ContentCards/useResourceHelper';
import useDidMountEffect from '../../../../libs/CustomHooks/UseDidMountEffect';
import { useScroll } from '../../../../libs/CustomHooks/useScroll';
import ArrowButtons from './ArrowButtons';
import CategoryDetail from './CategoryDetail';
import CategoryItem from './CategoryItem';

interface CategoryType {
	id?: number;
	_id?: string;
	name?: string;
	title?: string;
	[key: string]: any;
}

interface CustomCategoriesFilterProps {
	menuItems?: any[];
	labelText?: string;
	categoryId?: string | number;
	value?: CategoryType;
	onChange?: (category: CategoryType) => void;
}

const CustomCategoriesFilter: React.FC<CustomCategoriesFilterProps> = (props) =>
{
	const {
		menuItems,
		labelText,
		// categoryId,
		value: externalValue,
		onChange: externalOnChange
	} = props;
	const { categoryId } = useParams(); // Extract categoryId from URL parameters
	const location = useLocation(); // Get location object to access state
	const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>(null);
	const [ internalSelectedCategory, setInternalSelectedCategory ] = useState<CategoryType | null>(null);
	const [ categoryData, setCategoryData ] = useState<any[]>([]);
	const [ hoverCategory, setHoverCategory ] = useState<CategoryType | null>(null);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ error, setError ] = useState<string | null>(null);
	const [ page, setPage ] = useState<number>(1);
	const [ pagination, setPagination ] = useState({
		total : 0,
		page  : 0,
		limit : 0,
		pages : 0
	});

	// History state to track navigation
	const [ history, setHistory ] = useState<string[]>([]);
	const [ currentHistoryIndex, setCurrentHistoryIndex ] = useState<number>(-1);

	// Refs for infinite scroll
	const lastEleRef = useRef(null);
	const loaderRef = useRef<boolean>(false);
	const navigate = useNavigate();
	const { isAdminView } = useResourceHelper();

	// Get category data from URL state
	const urlCategoryData = location.state?.categoryData as CategoryType | undefined;

	// Use external value if provided, otherwise use internal state
	const selectedCategory = externalValue !== undefined ? externalValue : internalSelectedCategory;

	// Get session data from Redux store to access companyId
	const session = useSelector((state: any) => state?.mysession?.session);
	const companyId = session?.companyId || '';
	const networkCompanyId = useParams()?.companyId || '';

	// Apply useScroll hook for infinite scrolling
	useScroll({
		lastEleRef,
		loaderRef,
		count : pagination.total,
		data  : categoryData,
		setPage
	});

	// Initialize history when categoryId changes
	useEffect(() =>
	{
		if (categoryId)
		{
			setHistory((prev) =>
			{
				// If this categoryId is not already in history, add it
				if (!prev.includes(categoryId))
				{
					const newHistory = [ ...prev, categoryId ];

					setCurrentHistoryIndex(newHistory.length - 1);

					return newHistory;
				}
				else
				{
					// If it exists, find the index and remove all items after it
					const index = prev.indexOf(categoryId);
					const trimmedHistory = prev.slice(0, index + 1);

					setCurrentHistoryIndex(index);

					return trimmedHistory;
				}
			});
		}
		else
		{
			setHistory([]);
			setCurrentHistoryIndex(-1);
		}
	}, [ categoryId ]);

	const fetchCategoryData = async (targetCategoryId?: string) =>
	{
		// if (loaderRef.current) return;

		page === 1 && setLoading(true);
		loaderRef.current = true;

		try
		{
			// Use targetCategoryId if provided, otherwise use categoryId from params
			const currentCategoryId = targetCategoryId ?? categoryId;

			// Determine which companyId to use (network or current)
			const targetCompanyId = networkCompanyId || companyId;

			// Create options object for graphqlGetCategories with the correct types
			const graphqlOptions = {
				page,
				limit   : 10,
				from    : isAdminView ? 'Configuration' as const : 'MyResource' as const,
				filters : {
					...(currentCategoryId ? { parentCategoryId: currentCategoryId } : { isSubcategory: false }),
					...(!isAdminView && { isEnabled: true })
				}
			};

			const response = await graphqlGetCategories(targetCompanyId, graphqlOptions);

			// Append or replace data based on page number
			setCategoryData((prev) =>
				(page === 1
					? response.categories || []
					: [ ...prev, ...(response.categories || []) ]));

			// Set pagination info
			if (response.pagination)
			{
				setPagination(response.pagination);
			}

			// Set first category as selected if data exists and no external value is provided
			if (page === 1 && response && (response.categories?.length > 0))
			{
				const firstCategory = response.categories[0];

				setHoverCategory(firstCategory);

				// Call external handler with initial value if provided
				// if (externalOnChange) {
				// 	externalOnChange(firstCategory);
				// }
			}
		}
		catch (err)
		{
			console.error('Error fetching categories:', err);
			setError('Failed to load categories');
		}
		finally
		{
			setLoading(false);
			loaderRef.current = false;
		}
	};

	// Fetch categories/subcategories when component mounts or categoryId/companyId changes
	useEffect(() =>
	{
		fetchCategoryData(history[currentHistoryIndex] || '');
	}, [ page ]);

	useEffect(() =>
	{
		const handleSubCategoryCreated = (event: any) =>

		{
			const { parentCategoryId } = event.detail || {};

			// Optional: fetch only if it matches current parent
			if (!parentCategoryId || parentCategoryId === history[currentHistoryIndex])
			{
				fetchCategoryData(history[currentHistoryIndex] || '');
			}
		};

		// Register listener
		window.addEventListener('subcategoryCreated', handleSubCategoryCreated);

		// Cleanup
		return () =>
		{
			window.removeEventListener('subcategoryCreated', handleSubCategoryCreated);
		};
	}, [ currentHistoryIndex, history ]);
	// Fetch more data when page changes
	useDidMountEffect(() =>
	{
		// to set this when category id is changed from breadcrumbs
		page > 1
			? setPage(1)
			: fetchCategoryData();
		setHoverCategory(null);
		if (categoryId !== selectedCategory?._id)
		{
			setInternalSelectedCategory(null);
		}
	}, [ categoryId ]);

	useDidMountEffect(() =>
	{
		if (urlCategoryData?.breadcrumb?.pathIds)
		{
			const initialPathIds = urlCategoryData?.breadcrumb?.pathIds || [];
			const newHistory = [ ...initialPathIds, urlCategoryData?._id ];

			if (JSON.stringify(newHistory) !== JSON.stringify(history))
			{
				setHistory(newHistory);
				setCurrentHistoryIndex(newHistory.length - 1);
				fetchCategoryData(newHistory[newHistory.length - 1] || '');
			}
			setInternalSelectedCategory(urlCategoryData);
		}
	}, [ urlCategoryData ]);

	const handleSelectOpen = (event: React.SyntheticEvent) =>
	{
		setAnchorEl(event.currentTarget as HTMLElement);
		if (currentHistoryIndex !== history.length - 1)
		{
			setPage(1);
			setHoverCategory(null);
			setCategoryData([]);
			setCurrentHistoryIndex(history.length - 1);
			fetchCategoryData(history[history.length - 1] || '');
		}
	};

	const handleClose = () =>
	{
		setAnchorEl(null);
	};

	/**
	 * Handles category selection and manages history navigation
	 *
	 * History Management Behavior:
	 * 1. If user is at the end of history (currentHistoryIndex === history.length - 1):
	 *    - Append new category to history (normal forward navigation)
	 * 2. If user has gone back in history (currentHistoryIndex < history.length - 1):
	 *    - Replace forward history with new category (like browser behavior)
	 *    - This prevents creating multiple branches when navigating back and selecting new categories
	 */
	const handleCategorySelect = (category: CategoryType) =>
	{
		// Update internal state if we're not using external value
		if (externalValue === undefined)
		{
			setInternalSelectedCategory(category);
		}

		// Call external handler if provided
		if (externalOnChange)
		{
			externalOnChange(category);
		}

		// Add to history and navigate (redirection happens here)
		if (category._id)
		{
			setHistory((prev) =>
			{
				// If we're not at the end of history (user has gone back),
				// replace the forward history instead of appending
				if (currentHistoryIndex < prev.length - 1)
				{
					// Keep history up to current index, then add new category
					const newHistory = prev.slice(0, currentHistoryIndex + 1);

					newHistory.push(category._id!);

					setCurrentHistoryIndex(newHistory.length - 1);

					return newHistory;
				}
				else
				{
					// We're at the end, so just append normally
					const newHistory = [ ...prev, category._id! ];

					setCurrentHistoryIndex(newHistory.length - 1);

					return newHistory;
				}
			});
			navigate(isAdminView
				? `/businessSettings/content/categories/${category._id}`
				: networkCompanyId
					? `/resource/network/companies/${networkCompanyId}/categories/${category._id}`
					: `/resource/categories/${category._id}`, {
				state : { categoryData: category }
			});
		}

		// Close popover
		handleClose();
	};

	// Handle previous button click
	const handlePreviousClick = () =>
	{
		if (currentHistoryIndex >= 0)
		{
			const previousIndex = currentHistoryIndex - 1;
			const previousCategoryId = history[previousIndex] || '';

			setCurrentHistoryIndex(previousIndex);

			// Reset page and fetch data for previous category (no navigation)
			setPage(1);
			setCategoryData([]);
			fetchCategoryData(previousCategoryId);
		}
	};

	// Handle next button click
	const handleNextClick = () =>
	{
		if (currentHistoryIndex < history.length - 1)
		{
			const nextIndex = currentHistoryIndex + 1;
			const nextCategoryId = history[nextIndex];

			setCurrentHistoryIndex(nextIndex);

			// Reset page and fetch data for next category (no navigation)
			setPage(1);
			setCategoryData([]);
			fetchCategoryData(nextCategoryId);
		}
	};

	const open = Boolean(anchorEl);
	const id = open ? 'custom-popover' : undefined;

	// Use API data if available, otherwise use menuItems prop
	const displayItems = categoryData.length > 0 ? categoryData : [];

	// Check if navigation buttons should be enabled
	const canGoBack = currentHistoryIndex >= 0;
	const canGoForward = currentHistoryIndex < history.length - 1;

	return (
		<>
			<Grid item>
				<Typography color='var(--darkgray)' fontSize='var(--pixel10)'>
					{labelText}
				</Typography>
				<Select
					value={selectedCategory?._id || 0}
					open={false}
					onClick={handleSelectOpen}
					renderValue={() =>
					{
						// Priority: URL state data > selected category > default
						// if (urlCategoryData)
						// {
						// 	return urlCategoryData.title || urlCategoryData.name || 'Category';
						// }

						return selectedCategory?.title || selectedCategory?.name || 'All';
					}}
					sx={{
						width                                : 200,
						backgroundColor                      : 'var(--lightgray)',
						border                               : 'none',
						borderRadius                         : 'var(--pixel4)',
						boxShadow                            : 'none',
						minWidth                             : '220px',
						'& .MuiOutlinedInput-notchedOutline' : {
							border : 'none'
						},
						'&:hover' : {
							backgroundColor : '#3FBBE11A'
						},
						'& .MuiSelect-select' : {
							padding  : '4px 10px',
							fontSize : 'var(--pixel14)'
						}
					}}
					MenuProps={{
						disableAutoFocusItem : true,
						PaperProps           : {
							style       : { maxHeight: 300, overflow: 'auto' },
							onMouseDown : (e) => e.stopPropagation()
						}
					}}
				/>

				<Popover
					id={id}
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				>
					<Grid container px={1.5} py={2} sx={{ width: '600px', height: '380px', border: '1px solid #256EA61A', borderRadius: '6px', overflow: 'hidden' }}>
						<Grid item xs={5} sx={{ height       : '100%',
							overflowY    : 'hidden',
							paddingRight : '8px',
							'&:hover'    : {
								overflowY                    : 'auto',
								'&::-webkit-scrollbar-track' : { background: 'transparent' },
								'&::-webkit-scrollbar-thumb' : { background: '#D9D9D9', borderRadius: '4px' }
							} }}
						>
							<ArrowButtons
								canGoBack={canGoBack}
								canGoForward={canGoForward}
								onPreviousClick={handlePreviousClick}
								onNextClick={handleNextClick}
							/>
							{loading && page === 1
								? (
									<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
										<CircularProgress size={24} />
									</Box>
								)
								: error
									? (
										<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
											<Typography color='error'>{error}</Typography>
										</Box>
									)
									: displayItems.length === 0
										? (
											<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
												<Typography
													color='var(--darkgray)'
													fontSize='var(--pixel14)'
													textAlign='center'
												>
													No categories found
												</Typography>
											</Box>
										)
										: (
											<Stack spacing={1} sx={{ height: '100%' }}>
												{displayItems.map((category, index) => (
													<CategoryItem
														key={category.id || category._id || index}
														category={category}
														onSelect={handleCategorySelect}
														onMouseEnter={() => setHoverCategory(category)}
													/>
												))}

												{/* Invisible element for intersection observer */}
												{!loading && displayItems.length > 0 && displayItems.length < pagination.total && (
													<Box ref={lastEleRef} sx={{ border: '1px solid white', height: '4px', width: '100%' }} />
												)}

												{/* Show loading indicator at bottom when fetching more */}
												{loading && page > 1 && (
													<Box display='flex' justifyContent='center' py={1}>
														<CircularProgress size={20} />
													</Box>
												)}
											</Stack>
										)}
						</Grid>
						<Grid item xs={0.2}>
							<Divider orientation='vertical' flexItem sx={{ backgroundColor: '#e0e0e0' }} />
						</Grid>
						<Grid item xs={6.8} sx={{ borderLeft: '1px solid #e0e0e0', height: '100%' }}>
							{hoverCategory && <CategoryDetail
								selectedCategory={hoverCategory}
								labelText={labelText || ''}
							                  />}
						</Grid>
					</Grid>
				</Popover>
			</Grid>
		</>
	);
};

export default CustomCategoriesFilter;
